import { ChevronDown, Download } from "components/icons/generated";
import { RunContext } from "components/Run/Context";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import {
  getCpuProfileUnavailableReason,
  hasCpuProfile,
} from "shared/Run/useDownloadCpuProfile/helpers";
import useDownloadLogs from "shared/Run/useDownloadLogs";
import { getLogsUnavailableReason } from "shared/Run/useDownloadLogs/helpers";
import { RunState } from "types/generated";

import { showCpuProfileDrawer } from "../CpuProfileDrawer";

type RunDownloadButtonProps = {
  expired: boolean;
  deleted: boolean;
  ongoing: boolean;
  state: RunState;
  stateVersion?: number | null;
  runId: string;
  stackId: string;
  isModuleRun: boolean;
};

const RunDownloadButton = ({
  state,
  stateVersion,
  expired,
  deleted,
  ongoing,
  runId,
  stackId,
  isModuleRun,
}: RunDownloadButtonProps) => {
  const { run } = useTypedContext(RunContext);
  const trackSegmentAnalyticsEvent = useAnalytics();

  const canDownloadCpuProfile =
    !isModuleRun && hasCpuProfile(state) && !!run.runtimeConfig?.opentofu?.enableCPUProfiling;

  const logsUnavailableReason = getLogsUnavailableReason(expired, deleted);
  const cpuProfileUnavailableReason = getCpuProfileUnavailableReason(ongoing);

  const { downloadLogs, loading: logsLoading } = useDownloadLogs({
    state,
    stateVersion,
    runId,
    stackId,
    isModuleRun,
  });

  const openCpuProfileDrawer = () => {
    trackSegmentAnalyticsEvent("Run Logs - open CPU profile drawer", {
      cpuProfileState: state,
      runState: run.state,
    });

    showCpuProfileDrawer({ stackId, runId, phase: state, runState: run.state });
  };

  if (!canDownloadCpuProfile) {
    return (
      <Tooltip
        active={!!logsUnavailableReason}
        on={(props) => (
          <Button
            {...props}
            variant="secondary"
            startIcon={Download}
            size="small"
            onPress={downloadLogs}
            loading={logsLoading}
            disabled={logsLoading || !!logsUnavailableReason}
          >
            Download
          </Button>
        )}
      >
        {logsUnavailableReason}
      </Tooltip>
    );
  }

  // The CPU profile has its own retention, so unavailable logs do not disable it
  return (
    <DropdownMenu
      placement="bottom right"
      triggerComponent={
        <Button
          variant="secondary"
          startIcon={Download}
          endIcon={ChevronDown}
          size="small"
          loading={logsLoading}
          disabled={logsLoading}
        >
          Download
        </Button>
      }
    >
      <DropdownMenuItem
        onAction={downloadLogs}
        isDisabled={!!logsUnavailableReason}
        tooltip={logsUnavailableReason}
        isTooltipActive={!!logsUnavailableReason}
        tooltipWidthMode="maxWidthSm"
        tooltipPlacement="left"
      >
        Logs
      </DropdownMenuItem>
      <DropdownMenuItem
        onAction={openCpuProfileDrawer}
        isDisabled={!!cpuProfileUnavailableReason}
        tooltip={cpuProfileUnavailableReason}
        isTooltipActive={!!cpuProfileUnavailableReason}
        tooltipWidthMode="maxWidthSm"
        tooltipPlacement="left"
      >
        CPU profile
      </DropdownMenuItem>
    </DropdownMenu>
  );
};

export default RunDownloadButton;
